import { Component, Inject, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import * as models from '../../models';
import { FloorPlanService, NotifierService } from 'app/services';

// Uploader for floor-plan images. Unlike the 100 KB-capped image-upload dialog,
// this accepts large photos/scans and downscales them client-side (<= MAX_EDGE,
// JPEG) before upload so the stored asset stays small and Storage-friendly.
//
// data (optional): { floorPlan?: FloorPlan }  — pass an existing plan to rename
// / replace its image. Closes with the saved FloorPlan (id + dimensions set),
// or undefined on cancel.
@Component({
    selector: 'app-floor-plan-upload',
    templateUrl: './floor-plan-upload.component.html',
    styleUrls: ['./floor-plan-upload.component.scss'],
    standalone: false
})
export class FloorPlanUploadComponent implements OnInit {

  // downscale target: longest edge in px, and JPEG quality
  private static readonly MAX_EDGE = 2048;
  private static readonly JPEG_QUALITY = 0.85;
  // hard cap on the *original* pick (rules allow 12 MB; downscale shrinks it)
  private static readonly MAX_INPUT_BYTES = 20 * 1024 * 1024;

  editing: boolean = false;
  existing: models.FloorPlan;

  name: string = '';
  mode: 'upload' | 'blank' = 'upload';
  canvasWidth: number = 1200;
  canvasHeight: number = 800;
  showGrid: boolean = true;
  backgroundFit: 'fit' | 'fill' = 'fit';
  imgURL: string;              // data URL preview of the downscaled image
  fileName: string;
  processedBlob: Blob;         // downscaled JPEG ready to upload
  processedWidth: number;
  processedHeight: number;

  message: string;
  dragging: boolean = false;
  processing: boolean = false; // busy downscaling
  formSubmitted: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<FloorPlanUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: MatSnackBar,
    private floorPlanService: FloorPlanService,
    private notifier: NotifierService,
  ) {
    if (data && data.floorPlan) {
      this.existing = data.floorPlan;
      this.editing = true;
      this.name = data.floorPlan.name || '';
      this.mode = data.floorPlan.fullPath ? 'upload' : 'blank';
      this.canvasWidth = data.floorPlan.canvasWidth || data.floorPlan.imageWidth || 1200;
      this.canvasHeight = data.floorPlan.canvasHeight || data.floorPlan.imageHeight || 800;
      this.showGrid = data.floorPlan.showGrid !== false;
      this.backgroundFit = data.floorPlan.backgroundFit || 'fit';
    }
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('image-upload-redesign');
  }

  get processedSizeLabel(): string {
    if (this.processedBlob == null) return '';
    let kb = this.processedBlob.size / 1024;
    return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  // a new image is required only when creating; editing may keep the old one
  get canSubmit(): boolean {
    if (this.processing || this.formSubmitted) return false;
    if (!this.name || !this.name.trim()) return false;
    if (this.mode === 'blank') return this.canvasWidth >= 200 && this.canvasHeight >= 200;
    return this.editing && !!this.existing?.fullPath ? true : this.processedBlob != null;
  }

  browse() {
    document.getElementById('floorPlanFile').click();
  }

  onDragOver(event: DragEvent) { event.preventDefault(); this.dragging = true; }
  onDragLeave(event: DragEvent) { event.preventDefault(); this.dragging = false; }
  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    if (event.dataTransfer && event.dataTransfer.files) {
      this.pick(event.dataTransfer.files);
    }
  }

  clearSelection() {
    this.imgURL = null;
    this.fileName = null;
    this.processedBlob = null;
    this.processedWidth = null;
    this.processedHeight = null;
    this.message = null;
  }

  async pick(files: FileList) {
    if (files == null || files.length === 0) return;
    let file = files.item(0);

    if (file.type.match(/image\/.*/) == null) {
      this.message = 'Only image files are supported.';
      return;
    }
    if (file.size > FloorPlanUploadComponent.MAX_INPUT_BYTES) {
      this.message = 'That image is too large. Please choose one under 20 MB.';
      return;
    }

    this.message = null;
    this.fileName = file.name;
    this.processing = true;
    try {
      await this.downscale(file);
    } catch (e) {
      this.message = 'Could not read that image. Please try a different file.';
      this.clearSelection();
    } finally {
      this.processing = false;
    }
  }

  // Draw the picked image to a canvas capped at MAX_EDGE and export a JPEG.
  private downscale(file: File): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let url = URL.createObjectURL(file);
      let img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          let max = FloorPlanUploadComponent.MAX_EDGE;
          let ratio = Math.min(1, max / Math.max(width, height));
          let w = Math.round(width * ratio);
          let h = Math.round(height * ratio);

          let canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          let ctx = canvas.getContext('2d');
          // flatten onto white so transparent PNGs don't turn black as JPEG
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);

          canvas.toBlob(blob => {
            URL.revokeObjectURL(url);
            if (blob == null) { reject(new Error('toBlob failed')); return; }
            this.processedBlob = blob;
            this.processedWidth = w;
            this.processedHeight = h;
            this.imgURL = canvas.toDataURL('image/jpeg', 0.6); // light preview
            resolve();
          }, 'image/jpeg', FloorPlanUploadComponent.JPEG_QUALITY);
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
      img.src = url;
    });
  }

  async submit() {
    if (!this.canSubmit) return;
    this.formSubmitted = true;

    let floorPlan: models.FloorPlan = this.editing
      ? { ...this.existing }
      : {};
    floorPlan.name = this.name.trim();
    floorPlan.canvasWidth = this.canvasWidth;
    floorPlan.canvasHeight = this.canvasHeight;
    floorPlan.showGrid = this.showGrid;
    floorPlan.backgroundFit = this.backgroundFit;

    if (this.mode === 'upload' && this.processedBlob != null) {
      floorPlan.file = new File([this.processedBlob], (this.fileName || 'floor-plan') + '.jpg', { type: 'image/jpeg' });
      floorPlan.imageWidth = this.processedWidth;
      floorPlan.imageHeight = this.processedHeight;
      if (!this.editing) {
        floorPlan.canvasWidth = this.processedWidth;
        floorPlan.canvasHeight = this.processedHeight;
      }
    }

    try {
      let id = await this.floorPlanService.save(floorPlan);
      floorPlan.id = id;
      delete floorPlan.file;
      this.dialogRef.close(floorPlan);
    } catch (e) {
      this.formSubmitted = false;
      this.notifier.error('There was an issue uploading the floor plan. Please try again.');
    }
  }
}
